import type {
  AssistantMessageBlock,
  AssistantMarkdownImage,
} from '../../shared/assistantMessage';
import type { SceneOpenIntroPayload } from '../../state/sceneOpen';
import { getSceneBannerKind } from '../../scene/sceneBannerMap';

// Single image on a line (mirrors `MarkdownBlock` regex).
const IMAGE_LINE_RE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;
// Bold-wrap and heading-marker stripping for the welcome title.
const BOLD_WRAP_RE = /^\*\*(.+)\*\*$/;
const HEADING_RE = /^#+\s+(.+)$/;

export type ParsedOverallIntro = {
  /** First non-empty line of welcome content (markdown bold/heading stripped).
   *  Used as the welcome slide's 40px title. `null` if intro has no text. */
  welcomeTitle: string | null;
  /** Remaining welcome lines joined with single newlines — the welcome
   *  slide's 16px description. `null` if there's no body content. */
  welcomeDescription: string | null;
  /** Scene-banner images (kinds `main` / `inside`) — drive the per-scene
   *  slides. Title / description / moveToSceneId already populated by
   *  backend; thumbnail image fields are ignored (intro is now text-only). */
  banners: AssistantMarkdownImage[];
};

/**
 * Splits a `scene_open` reply into welcome title + description + banner
 * images. Welcome content is split on the first blank line:
 *   line 1 (stripped of `**...**` / `# ...`) → title
 *   remaining lines → description
 *
 * Banner images get filtered to `main` / `inside` kinds; `artifact` images
 * are dropped (root-map design has no artifact cards).
 */
export const parseOverallIntro = (
  intro: SceneOpenIntroPayload | null,
): ParsedOverallIntro => {
  if (!intro) {
    return { welcomeTitle: null, welcomeDescription: null, banners: [] };
  }

  const textChunks: string[] = [];
  const allImages: AssistantMarkdownImage[] = [];

  for (const block of intro.blocks) {
    if (block.type === 'paragraph') {
      const trimmed = block.text.trim();
      if (trimmed) textChunks.push(trimmed);
      continue;
    }
    if (block.type === 'markdown') {
      // Strip image-line markdown from text — those become separate slides.
      const textOnly = block.content
        .split('\n')
        .filter((line) => !IMAGE_LINE_RE.test(line.trim()))
        .join('\n')
        .trim();
      if (textOnly) textChunks.push(textOnly);
      allImages.push(...block.images);
      continue;
    }
    // `photos` blocks ignored on overall (don't fit the slider layout).
  }

  const welcomeContent = textChunks.length > 0 ? textChunks.join('\n\n') : '';
  const { welcomeTitle, welcomeDescription } = splitTitleAndDescription(welcomeContent);

  const banners = allImages.filter((img) => {
    if (!img.moveToSceneId) return false;
    const kind = getSceneBannerKind(img.moveToSceneId);
    return kind === 'main' || kind === 'inside';
  });

  return { welcomeTitle, welcomeDescription, banners };
};

/**
 * First non-empty trimmed line → title (markdown `**bold**` / `#`-heading
 * stripped). Everything after the first blank-line separator → description.
 * Without a blank line, description is whatever follows the first line.
 */
function splitTitleAndDescription(text: string): {
  welcomeTitle: string | null;
  welcomeDescription: string | null;
} {
  const lines = text.split('\n').map((line) => line.trim());
  let titleIdx = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i]) {
      titleIdx = i;
      break;
    }
  }
  if (titleIdx === -1) return { welcomeTitle: null, welcomeDescription: null };

  const rawTitle = lines[titleIdx];
  const titleNoBold = rawTitle.match(BOLD_WRAP_RE)?.[1] ?? rawTitle;
  const titleNoHeading = titleNoBold.match(HEADING_RE)?.[1] ?? titleNoBold;
  const welcomeTitle = titleNoHeading.replace(/[*_]/g, '').trim() || null;

  const rest = lines.slice(titleIdx + 1).join('\n').trim();
  return {
    welcomeTitle,
    welcomeDescription: rest.length > 0 ? rest : null,
  };
}

// Re-export for callers that still want the block type alongside the result.
export type { AssistantMessageBlock };
